import googleReviews from "./google-reviews.json";
import { useEffect, useRef, useState } from "react";
import acamasMessage from "@/assets/testimonial-acamas.webp";
import { getHomeCopy, type HomeLanguage } from "./homeTranslations";
import acamas from "@/assets/avatar-acamas.webp";
import philippe from "@/assets/avatar-philippe.webp";
import gravel from "@/assets/avatar-gravel.webp";
import prodrive from "@/assets/avatar-prodrive.webp";
import seraudit from "@/assets/avatar-seraudit.webp";
import atenia from "@/assets/avatar-atenia.webp";
import chabaud from "@/assets/avatar-chabaud.webp";
import philippeMessage from "@/assets/testimonial-philippe.webp";

const googleProfile = "https://maps.app.goo.gl/oALrDWNok3547FpGA";
const clients = [
  [atenia, "Atēnia"], [chabaud, "Clinique vétérinaire Chabaud"],
  [acamas, "Acamas Immobilier"], [philippe, "Philippe Asselin"],
  [prodrive, "ProDrive"], [seraudit, "Seraudit"],
];

export function ClientBubbles() {
  return <span className="client-bubbles" aria-hidden="true">
    {clients.map(([src, name]) => <img key={name} src={src} alt="" width="32" height="32" decoding="async" />)}
    <span>+15</span>
  </span>;
}

const testimonials = [
  { name: "Noémie & Raoul", company: "Acamas Immobilier", avatar: acamas,
    text: "« Exactement ce qu’on cherchait, moderne et professionnel. On a déjà reçu nos premières demandes en ligne. »",
    proof: acamasMessage, proofLabel: "Message original de Noémie et Raoul", googleUrl: null, lang: undefined },
  { name: "Michael Gravel", company: "iA Groupe Financier", avatar: gravel,
    text: "Un site professionnel qui reflète parfaitement notre image et qui nous génère de nouveaux prospects chaque semaine. L'accompagnement a été impeccable du début à la fin.",
    proof: null, proofLabel: "", googleUrl: null, lang: undefined },
  { name: "Philippe Asselin", company: "MRL Hypothèque", avatar: philippe,
    text: "Je suis très satisfait du travail accompli. Tu peux compter sur moi pour te référer et pour mes projets futurs. Sérieux, efficace et à l'écoute.",
    proof: philippeMessage, proofLabel: "Message original de Philippe Asselin", googleUrl: null, lang: undefined },
  { name: "ProDrive SL", company: "ProDrive Andorre", avatar: prodrive,
    text: googleReviews.reviews[0].excerpt,
    proof: null, proofLabel: "", googleUrl: googleReviews.reviews[0].url, lang: googleReviews.reviews[0].language },
];

export function ClientTestimonials({ language }: { language: HomeLanguage }) {
  const copy = getHomeCopy(language);
  const [selected, setSelected] = useState<(typeof testimonials)[number] | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const track = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (selected) dialog.current?.showModal();
    else if (dialog.current?.open) dialog.current.close();
  }, [selected]);
  const scrollByCard = (direction: 1 | -1) => {
    const el = track.current;
    if (!el) return;
    const card = el.querySelector(".story-card") as HTMLElement | null;
    const amount = (card?.offsetWidth || el.clientWidth) + 18;
    el.scrollBy({ left: amount * direction, behavior: "smooth" });
  };
  return <section id="testimonials" className="client-stories section wrap">
    <div className="stories-heading">
      <div><span className="label">{copy("CRÉATION WEB / LES MOTS DE NOS CLIENTS")}</span><p className="stories-title">{copy("Avis clients")}<span aria-hidden="true">.</span></p></div>
      <div className="stories-rating"><span className="social-rating-stars" aria-hidden="true">★★★★★</span><span><strong>5/5</strong> — {copy("Avis clients")}</span></div>
      {testimonials.length > 3 && <div className="stories-nav">
        <button type="button" aria-label={copy("Avis précédent")} onClick={() => scrollByCard(-1)}>←</button>
        <button type="button" aria-label={copy("Avis suivant")} onClick={() => scrollByCard(1)}>→</button>
      </div>}
    </div>
    <div className={`stories-grid${testimonials.length > 3 ? " stories-carousel" : ""}`} ref={track}>
      {testimonials.map((review, index) => <article className={`story-card${index === 0 ? " story-card-featured" : ""}`} key={review.name}>
        <div className="story-card-top"><span className="social-rating-stars" aria-hidden="true">★★★★★</span><span className="story-number">0{index + 1}<span className="sr-only"> — 5/5</span></span></div>
        <blockquote lang={review.lang}>{review.lang ? review.text : copy(review.text)}</blockquote>
        <div className="story-card-bottom">
          <div className="review-author"><img src={review.avatar} alt="" width="44" height="44" loading="lazy" /><p><strong>{copy(review.name)}</strong><span>{review.company}</span></p></div>
          {review.proof && <button type="button" className="story-proof-link" onClick={() => setSelected(review)}>{copy("Voir le message original ↗")}</button>}
          {review.googleUrl && <a className="story-proof-link" href={review.googleUrl} target="_blank" rel="noopener noreferrer">{copy("Voir sur Google Maps ↗")}</a>}
        </div>
      </article>)}
    </div>
    <dialog className="review-proof-dialog" ref={dialog} aria-label={copy(selected?.proofLabel || "Avis clients")} onCancel={() => setSelected(null)} onClose={() => setSelected(null)} onClick={event => { if (event.target === event.currentTarget) setSelected(null); }}>
      {selected && <div className="review-proof-panel"><div className="review-proof-toolbar"><p>{copy(selected.proofLabel)}</p><button type="button" aria-label={copy("Fermer")} onClick={() => setSelected(null)}>×</button></div><img src={selected.proof!} alt={copy(selected.proofLabel)} /><a href={selected.proof!} target="_blank" rel="noopener noreferrer">{copy("Voir le message original ↗")}</a></div>}
    </dialog>
  </section>;
}

export function GoogleReviews({ language }: { language: HomeLanguage }) {
  const copy = getHomeCopy(language);
  return <section id="google-reviews" className="section wrap google-reviews" aria-labelledby="google-reviews-title">
    <div className="google-reviews-heading">
      <div><span className="label">{copy("AVIS GOOGLE")}</span><h2 id="google-reviews-title">{copy("Vos retours, sur Google.")}</h2></div>
      <a className="google-rating-summary" href={googleProfile} target="_blank" rel="noopener noreferrer"><strong>{googleReviews.rating.toFixed(1)}</strong><span><span className="social-rating-stars" aria-hidden="true">★★★★★</span><span>{googleReviews.total} {copy("avis sur Google")} ↗</span></span></a>
    </div>
    <div className="google-reviews-grid">
      {googleReviews.reviews.map(review => <article className="google-review-tile" key={review.id}>
        <div className="review-author"><span className="review-initial" aria-hidden="true">{review.author.charAt(0).toUpperCase()}</span><p><strong>{review.author}</strong><span>Google · <time dateTime={review.date}>{new Intl.DateTimeFormat(language, { month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(review.date))}</time></span></p><span className="google-source-mark" aria-hidden="true">G</span></div>
        <div className="google-tile-rating"><span className="social-rating-stars" aria-hidden="true">{"★".repeat(review.rating)}</span><span className="sr-only">{review.rating}/5</span></div>
        <blockquote lang={review.language}>{review.excerpt}</blockquote>
        <a href={review.url} target="_blank" rel="noopener noreferrer">{copy("Voir sur Google Maps ↗")}</a>
      </article>)}
      <a className="google-all-reviews" href={googleProfile} target="_blank" rel="noopener noreferrer"><span className="label">GOOGLE</span><strong>{googleReviews.total}</strong><span>{copy("avis sur Google")}</span><span className="google-all-reviews-cta">{copy("Voir tous nos avis sur Google ↗")}</span></a>
    </div>
  </section>;
}
